import { SegmentKey } from '@deltasierra/i18n';
import moment from 'moment-timezone';
import { Brand as TypeBrand, createBranded } from '@deltasierra/type-utilities';
import { HasId } from '../../../common/model/traits';

export type ClubReadyListId = TypeBrand<number, 'ClubReadyListId'>;
export const ClubReadyListId = createBranded<ClubReadyListId>();

export interface ClubReadyListText {
    allDates?: string;
    dateRange?: string;
    before?: string;
    after?: string;
}
/**
 * The ClubReadyDateChoiceType specifies what input option is expected for the list.
 * The front-end uses this to determine how to render the form and the back-end
 * uses this to determine how it should filter for the list.
 *
 * 'Range' means there is an optional date range filter associated with the list selection.
 * 'BeforeAfter' means that there is an optional filter for before/after a specified date
 * 'None' means there is no additional date filtering associated with the list.
 */
export enum ClubReadyDateChoiceType {
    None = 'none',
    Range = 'range',
    BeforeAfter = 'before-after',
    EmailList = 'email-list',
}

export enum BeforeAfterPeriodType {
    Before = 'before',
    After = 'after',
}

export interface BaseClubReadyList extends HasId<ClubReadyListId> {
    dateChoice: ClubReadyDateChoiceType;
    name: string;
    keyId?: SegmentKey;
    text: ClubReadyListText;
}

export interface ClubReadyList extends BaseClubReadyList {
    recipientCount: number;
}

export interface ClubReadyListDateRangeChoice extends HasId<ClubReadyListId> {
    dateChoice: ClubReadyDateChoiceType.Range;
    range?: {
        from: Date;
        to: Date;
    };
}

export interface ClubReadyListBeforeAfterDateChoice extends HasId<ClubReadyListId> {
    dateChoice: ClubReadyDateChoiceType.BeforeAfter;
    datePeriod?: {
        period: BeforeAfterPeriodType;
        date: Date;
    };
}

export interface ClubReadyListAllDateChoice extends HasId<ClubReadyListId> {
    dateChoice: ClubReadyDateChoiceType.None;
}

export type ClubReadyDateChoice =
    | ClubReadyListAllDateChoice
    | ClubReadyListBeforeAfterDateChoice
    | ClubReadyListDateRangeChoice;

export function clubReadyListDateChoiceToName(list: ClubReadyDateChoice): string {
    let listName = '';
    switch (list.id) {
        case ClubReadyListId.from(1):
            listName = 'Active members';
            break;
        case ClubReadyListId.from(2):
            listName = 'Inactive members';
            break;
        case ClubReadyListId.from(3):
            listName = 'Prospects';
            break;
        case ClubReadyListId.from(4):
            listName = 'Cooled off prospects';
            break;
        case ClubReadyListId.from(5):
            listName = 'Custom list of email addresses';
            break;
        default:
            throw new Error('Unknown ClubReadyListId');
    }

    if (list.dateChoice === ClubReadyDateChoiceType.Range) {
        if (list.range) {
            listName = `${listName} - ${moment(list.range.from).format('Do MMM YYYY')} to ${moment(
                list.range.to,
            ).format('Do MMM YYYY')}`;
        }
    } else if (list.dateChoice === ClubReadyDateChoiceType.BeforeAfter) {
        if (list.datePeriod?.period === BeforeAfterPeriodType.Before) {
            listName = `${listName} - activity before ${moment(list.datePeriod.date).format('Do MMM YYYY')}`;
        } else if (list.datePeriod?.period === BeforeAfterPeriodType.After) {
            listName = `${listName} - activity after ${moment(list.datePeriod.date).format('Do MMM YYYY')}`;
        }
    }
    return listName;
}
